import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	IconButton,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { useState } from "react";
import { usePasswordGeneratorViewModel } from "./PasswordGenerator.vm";
import { PortalTooltip } from "../../helpers";

export const PasswordGenerator: React.FC = () => {
	const vm = usePasswordGeneratorViewModel();
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		if (!vm.generatedPassword) return;
		navigator.clipboard.writeText(vm.generatedPassword);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	};

	if (!vm.isSuperUser) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				p: { xs: 2, sm: 4 },
				maxWidth: "md",
				margin: "auto",
			}}
		>
			<Typography variant="h5" gutterBottom>
				Rogo Login Generator
			</Typography>

			<Card variant="outlined">
				<CardContent>
					<Stack spacing={3}>
						<Autocomplete
							options={vm.teamMembers}
							getOptionLabel={(option) => option.name || "Unknown"}
							value={vm.selectedUser}
							onChange={(_, newValue) => {
								vm.setSelectedUser(newValue);
							}}
							loading={vm.loadingTeamMembers}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select Team Member"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{vm.loadingTeamMembers ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							disabled={vm.loadingTeamMembers}
						/>

						<Button
							variant="contained"
							onClick={vm.generateAndSave}
							disabled={!vm.selectedUser || vm.saving}
							startIcon={vm.saving ? <CircularProgress size={20} color="inherit" /> : undefined}
						>
							{vm.saving ? "Generating..." : "Generate Password"}
						</Button>

						{vm.error && <Alert severity="error">{vm.error}</Alert>}

						{vm.generatedPassword && vm.saved && (
							<Alert
								severity="success"
								action={
									<PortalTooltip title={copied ? "Copied!" : "Copy password"}>
										<IconButton onClick={handleCopy} size="small" color="inherit">
											<ContentCopy fontSize="small" />
										</IconButton>
									</PortalTooltip>
								}
							>
								<Typography variant="body2" color="text.secondary">
									Password saved for {vm.selectedUser?.name}:
								</Typography>
								<Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 700, letterSpacing: 2 }}>
									{vm.generatedPassword}
								</Typography>
							</Alert>
						)}
					</Stack>
				</CardContent>
			</Card>
		</Box>
	);
};
